import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import Button from "../../Elements/Button";
import CommonModal from "../CommonModal";
import Select from "../../Elements/Select";
import { networkService } from "../../config/axios";
import UserInfoContext from "../../../utis/userInfoContext";
import apiGETRequest from "../../../utis/apiRequest";
import Input from "../../Elements/Input";

const MyProfileModal = ({ profileModalStaus, setProfileModalStaus, setOpenTerms }) => {
    const [timeZoneData, setTimeZoneData] = useState({});
    const [step, setStep] = useState(1);
    const [userModalField, setUserModalField] = useState({});
    const { profileData, userInfoData, setUserData, setUserProfileData, setShowNotifications } = useContext(UserInfoContext);
    const isFetchingRef = useRef(false);

    const updatingUserProfile = useCallback(async () => {
        // Prevent duplicate calls - check if already fetching
        if (isFetchingRef.current) {
            return;
        }
        
        isFetchingRef.current = true;
        try {
            let userData = await apiGETRequest(`edit.json`);
            if (userData && !userData.error) {
                setUserData(userData);
                let userProfileData = await apiGETRequest(
                    `profiles/${userData?.id}.json`
                );
                if (userProfileData && !userProfileData.error) {
                    setUserProfileData(userProfileData);
                }
            }
        } catch (error) {
            console.error("Error updating user profile:", error);
        } finally {
            isFetchingRef.current = false;
        }
    }, [setUserData, setUserProfileData]);

    const timeZoneCache = useRef(null);

    const updateTimeZone = async () => {
        if (timeZoneCache.current) {
            setTimeZoneData(timeZoneCache.current);
            return;
        }
        const getTimeZoneData = await apiGETRequest(`time-zones`);
        timeZoneCache.current = getTimeZoneData;
        setTimeZoneData(getTimeZoneData);
    };

    const handleOnchangeEvent = (element) => {
        let userModalFieldCopy = JSON.parse(JSON.stringify(userModalField));
        let valueToUpdate = userModalFieldCopy[`step${step}`].map((field) => {
            if (field.type === "name_row") {
                return {
                    ...field,
                    fields: field.fields.map((f) =>
                        f.name === element.target.name
                            ? { ...f, value: element.target.value, error: (element.target.value.trim() === "") && f.required }
                            : { ...f }
                    ),
                };
            }
            return field.name === element.target.name
                ? { ...field, value: element.target.value, error: (element.target.value.trim() === "") && field.required }
                : { ...field };
        });
        setUserModalField({
            ...userModalFieldCopy,
            [`step${step}`]: valueToUpdate,
        });
    };

    const updateModalFields = () => {
        let valueToUpdate = {
            step1: [
                {
                    type: "name_row",
                    fields: [
                        {
                            type: "text",
                            required: true,
                            label: "First Name",
                            name: "first_name",
                            value: profileData.first_name,
                        },
                        {
                            type: "text",
                            required: true,
                            label: "Last Name",
                            name: "last_name",
                            value: profileData.last_name,
                        },
                    ],
                },
                {
                    type: "text",
                    required: false,
                    label: "Your Company Privacy Notice",
                    placeholder: "Your Company Privacy Notice URL here",
                    name: "privacy_link",
                    value: profileData.privacy_link,
                },
                {
                    type: "select",
                    required: true,
                    label: "*Time Zone",
                    name: "time_zone",
                    value: profileData.time_zone,
                },
            ],
            step2: [
                {
                    type: "text",
                    required: true,
                    label: "Company Name",
                    name: "company_name",
                    value: profileData.company_name,
                },
                {
                    type: "text",
                    required: true,
                    label: "Country",
                    name: "country",
                    value: profileData.country,
                },
                {
                    type: "text",
                    required: false,
                    label: "Direct Chat Link (Tawk.to)",
                    name: "direct_chat_link",
                    value: profileData.direct_chat_link,
                },
            ],
        };
        setUserModalField(valueToUpdate);
    };

    const handleMoveToNext = async () => {
        let userModalFieldCopy = JSON.parse(JSON.stringify(userModalField));
        const currentStepFields = userModalFieldCopy[`step${step}`];

        // Validate fields (including nested name_row fields)
        const hasErrors = currentStepFields.some((field) => {
            if (field.type === "name_row") {
                return field.fields.some((f) => {
                    if (f.required && !f?.value?.trim()) {
                        f.error = true;
                        return true;
                    }
                    return false;
                });
            }
            if (field.required && !field?.value?.trim()) {
                field.error = true;
                return true;
            }
            if (field.name === "time_zone" && step === 1 && !field.value) {
                field.error = true;
                return true;
            }
            return false;
        });

        if (!hasErrors) {
            if (step < 2) {
                setStep((prevState) => prevState + 1);
            } else {
                // Prepare data to upload — flatten name_row fields
                let dataToUpload = Object.keys(userModalFieldCopy)
                    .map((fieldID) => userModalFieldCopy[fieldID])
                    .flat()
                    .flatMap((field) => field.type === "name_row" ? field.fields : [field])
                    .reduce((acc, curr) => {
                        acc[`${curr.name}`] = curr.value;
                        return acc;
                    }, {});
                let { data, status } = await networkService(
                    `profiles/${userInfoData.id}.json`,
                    "PATCH",
                    { profile: { ...dataToUpload }, user_id: userInfoData.id }
                );
                if (status === 200 && data) {
                    setUserProfileData(data);
                    setShowNotifications({
                        showToast: true,
                        toastType: "success",
                        toastMessage: "Profile details saved",
                    });
                    setOpenTerms(userInfoData?.privacy_accepted == false)
                }
                updatingUserProfile();
                setProfileModalStaus(false);
            }
        } else {
            setUserModalField(userModalFieldCopy);
        }
    };

    useEffect(() => {
        if (profileModalStaus) {
            updateModalFields();
            setStep(1);
        }
    }, [profileModalStaus, profileData]);

    useEffect(() => {
        return () => {
            setStep(1);
        };
    }, []);

    useEffect(() => {
        // Only fetch if modal is open AND data is missing (to prevent duplicate calls)
        if (profileModalStaus && !userInfoData?.id) {
            updatingUserProfile();
        }
        if (profileModalStaus) {
            updateTimeZone();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profileModalStaus]);

    return (
        <>
            <CommonModal
                title={"My Profile"}
                modalStatus={profileModalStaus}
                changeActiveStatus={setProfileModalStaus}
                classList="header-profile-modal-wrapper"
                closeModalOnEsc={false}
                footer={
                    <>
                        {step == 1 ? (
                            <Button
                                classList={"success"}
                                addonClass="d-inline-flex ml-4"
                                onClick={handleMoveToNext}
                                type="submit" 
                                text={"Continue"}
                            ></Button>
                        ) : (
                            <>
                                <Button
                                    classList={"success"}
                                    addonClass="d-inline-flex ml-4 common-modal-yes-button"
                                    onClick={() => setStep((prev) => prev - 1)}
                                    text={"Back"}
                                ></Button>
                                <Button
                                    classList={"success"}
                                    addonClass="d-inline-flex ml-4 common-modal-yes-button"
                                    onClick={handleMoveToNext}
                                    type="submit" 
                                    text={"Save Changes"}
                                ></Button>
                            </>
                        )}
                    </>
                }
                centered
            >
                <form name="profileForm">
                    {userModalField[`step${step}`] != undefined &&
                        userModalField[`step${step}`].map((item, index) => (
                            <div key={`step-${step}-${index}`}>
                                {item.type === "name_row" ? (
                                    <>
                                        <div className="d-flex gap-3">
                                            {item.fields.map((f, fi) => (
                                                <div key={`name-${fi}`} style={{ flex: 1 }}>
                                                    <Input
                                                        classList=""
                                                        type={f.type}
                                                        placeholder={`Enter ${f.label}`}
                                                        name={f.name}
                                                        required={f.required}
                                                        label={f.label}
                                                        onChange={(e) => handleOnchangeEvent(e)}
                                                        value={f.value}
                                                        errorMessageStatus={f?.error != undefined ? f.error : false}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        {step === 1 && (
                                            <div className="mb-3">
                                                <label className="control-label mb-2">Email</label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    value={userInfoData?.email || ""}
                                                    readOnly
                                                    tabIndex={-1}
                                                    onKeyDown={(e) => e.preventDefault()}
                                                    onPaste={(e) => e.preventDefault()}
                                                    style={{ backgroundColor: '#f3f4f6', color: '#6b7280', cursor: 'not-allowed', userSelect: 'none', marginTop: '10px', marginBottom: '10px' }}
                                                />
                                            </div>
                                        )}
                                    </>
                                ) : item.type === "select" ? (
                                    <>
                                        <label className="control-label mb-2">{item?.label}</label>
                                        <Select
                                            classList=""
                                            name={item.name}
                                            required={item.required}
                                            label={item.label}
                                            optionList={Object.keys(timeZoneData).map((item) => {
                                                return {
                                                    value: timeZoneData[item].id,
                                                    title: timeZoneData[item].name,
                                                };
                                            })}
                                            onChange={(e) => handleOnchangeEvent(e)}
                                            value={item.value}
                                            errorMessageStatus={
                                                item?.error != undefined ? item.error : false
                                            }
                                        />
                                    </>
                                ) : (
                                    <Input
                                        classList=""
                                        type={item.type}
                                        placeholder={item.placeholder || `Enter ${item.label}`}
                                        name={item.name}
                                        required={item.required}
                                        label={item.label}
                                        onChange={(e) => handleOnchangeEvent(e)}
                                        value={item.value}
                                        errorMessageStatus={
                                            item?.error != undefined ? item.error : false
                                        }
                                    />
                                )}
                            </div>
                        ))}
                </form>
            </CommonModal>
        </>
    )
}

export default React.memo(MyProfileModal)
